/**
 * Enhanced Product Card - Variant Selection & Add to Cart
 * Handles inline variant selection and AJAX add-to-cart on product cards.
 */

if (!customElements.get('enhanced-product-card')) {
    class EnhancedProductCard extends HTMLElement {
        constructor() {
            super();
            this.variantButtons = this.querySelectorAll('.card-enhanced__variant-btn');
            this.addToCartBtn = this.querySelector('.card-enhanced__add-btn');
            this.variantIdInput = this.querySelector('.card-enhanced__variant-id');
            this.variantsData = this.getVariantsData();

            this.bindEvents();
        }

        getVariantsData() {
            const el = this.querySelector('[data-enhanced-variants]');
            if (!el) return [];
            try {
                return JSON.parse(el.textContent);
            } catch (e) {
                return [];
            }
        }

        bindEvents() {
            this.variantButtons.forEach((btn) => {
                btn.addEventListener('click', this.onVariantClick.bind(this));
            });

            if (this.addToCartBtn) {
                this.addToCartBtn.addEventListener('click', this.onAddToCart.bind(this));
            }
        }

        onVariantClick(event) {
            event.preventDefault();
            event.stopPropagation();

            const btn = event.currentTarget;
            if (btn.disabled) return;

            const optionIndex = parseInt(btn.dataset.optionIndex, 10);
            const optionValue = btn.dataset.optionValue;

            // Update active state within the same option group
            this.querySelectorAll(`.card-enhanced__variant-btn[data-option-index="${optionIndex}"]`).forEach((b) => {
                b.classList.remove('is-active');
            });
            btn.classList.add('is-active');

            // Find matching variant
            this.updateSelectedVariant();
        }

        updateSelectedVariant() {
            // Collect selected options
            const selectedOptions = [];
            const optionGroups = this.querySelectorAll('.card-enhanced__option-group');

            optionGroups.forEach((group) => {
                const activeBtn = group.querySelector('.card-enhanced__variant-btn.is-active');
                if (activeBtn) {
                    selectedOptions.push(activeBtn.dataset.optionValue);
                }
            });

            // Find matching variant
            const matchedVariant = this.variantsData.find((variant) => {
                return variant.options.every((opt, index) => {
                    return selectedOptions[index] === undefined || selectedOptions[index] === opt;
                });
            });

            if (matchedVariant && this.variantIdInput) {
                this.variantIdInput.value = matchedVariant.id;

                // Update add to cart button state
                if (this.addToCartBtn) {
                    const btnText = this.addToCartBtn.querySelector('.card-enhanced__add-btn-text');
                    if (matchedVariant.available) {
                        this.addToCartBtn.disabled = false;
                        if (btnText) btnText.textContent = '+ Add to Cart';
                    } else {
                        this.addToCartBtn.disabled = true;
                        if (btnText) btnText.textContent = 'Sold Out';
                    }
                }

                // Update price display
                this.updatePriceDisplay(matchedVariant);
            }
        }

        updatePriceDisplay(variant) {
            const priceContainer = this.querySelector('.card-enhanced__price-variants .price');
            if (!priceContainer) return;

            const regularPriceEl = priceContainer.querySelector('.price__regular .price-item--regular');
            const salePriceEl = priceContainer.querySelector('.price__sale .price-item--sale');
            const compareEl = priceContainer.querySelector('.price__sale .price-item--regular');

            // Format currency
            const formatMoney = (cents) => {
                return new Intl.NumberFormat(Shopify.locale || 'en-IN', {
                    style: 'currency',
                    currency: Shopify.currency?.active || 'INR',
                }).format(cents / 100);
            };

            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                priceContainer.classList.add('price--on-sale');
                priceContainer.classList.remove('price--sold-out');
                if (salePriceEl) salePriceEl.textContent = formatMoney(variant.price);
                if (compareEl) compareEl.innerHTML = `<s>${formatMoney(variant.compare_at_price)}</s>`;
            } else {
                priceContainer.classList.remove('price--on-sale');
                if (regularPriceEl) regularPriceEl.textContent = formatMoney(variant.price);
            }

            if (!variant.available) {
                priceContainer.classList.add('price--sold-out');
            } else {
                priceContainer.classList.remove('price--sold-out');
            }
        }

        async onAddToCart(event) {
            event.preventDefault();
            event.stopPropagation();

            if (this.addToCartBtn.disabled) return;

            const variantId = this.variantIdInput?.value;
            if (!variantId) return;

            // Show loading
            this.addToCartBtn.classList.add('is-loading');
            this.addToCartBtn.disabled = true;

            try {
                const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
                const requestBody = {
                    id: parseInt(variantId, 10),
                    quantity: 1
                };

                if (cart) {
                    requestBody.sections = cart.getSectionsToRender().map((section) => section.id).join(',');
                    requestBody.sections_url = window.location.pathname;
                    cart.setActiveElement(document.activeElement);
                }

                const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/javascript',
                    },
                    body: JSON.stringify(requestBody),
                });

                if (!response.ok) {
                    throw new Error('Add to cart failed');
                }

                const data = await response.json();

                if (cart) {
                    if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
                    if (window.FlyToCart) {
                        FlyToCart.animate({
                            triggerElement: this.addToCartBtn,
                            onComplete: () => {
                                cart.renderContents(data);
                            },
                        });
                    } else {
                        cart.renderContents(data);
                    }
                } else {
                    this.updateCartCount();
                }

                if (window.publish && window.PUB_SUB_EVENTS) {
                    window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                        source: 'card-product-enhanced',
                        productVariantId: variantId,
                        cartData: data,
                    });
                } else {
                    document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));
                }

                // Brief success state
                this.addToCartBtn.classList.add('is-success');
                const btnText = this.addToCartBtn.querySelector('.card-enhanced__add-btn-text');
                if (btnText) {
                    const originalText = btnText.innerHTML;

                    // Reset animation gracefully
                    btnText.style.animation = 'none';
                    void btnText.offsetWidth; // trigger reflow
                    btnText.style.animation = null;

                    btnText.innerHTML = '✓ Added to Cart';
                    setTimeout(() => {
                        this.addToCartBtn.classList.remove('is-success');
                        btnText.innerHTML = originalText;
                    }, 2000);
                }
            } catch (error) {
                console.error('Enhanced card add to cart error:', error);
                const btnText = this.addToCartBtn.querySelector('.card-enhanced__add-btn-text');
                if (btnText) {
                    const originalText = btnText.textContent;
                    btnText.textContent = 'Error — try again';
                    setTimeout(() => {
                        btnText.textContent = originalText;
                    }, 2000);
                }
            } finally {
                this.addToCartBtn.classList.remove('is-loading');
                // Re-check current variant availability before re-enabling
                this.updateSelectedVariant();
            }
        }

        async updateCartCount() {
            try {
                const response = await fetch(window.Shopify.routes.root + 'cart.js', {
                    headers: { Accept: 'application/json' },
                });
                const cart = await response.json();

                // Update all cart count bubbles
                document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach((el) => {
                    el.textContent = cart.item_count;
                });
                document.querySelectorAll('.cart-count-bubble .visually-hidden').forEach((el) => {
                    el.textContent = `${cart.item_count} items`;
                });

                // Show cart bubble if hidden
                document.querySelectorAll('.cart-count-bubble').forEach((el) => {
                    if (cart.item_count > 0) {
                        el.classList.remove('hidden');
                    }
                });
            } catch (e) {
                // Silent fail for cart count update
            }
        }
    }

    customElements.define('enhanced-product-card', EnhancedProductCard);
}
